import clsx from 'clsx'

import { apiValueNumber } from '@/services/api/schemas/common'
import type { Column, Columns } from '@/services/api/schemas/station'
import { formatUpdatedAt, getErrorMessage } from '@/shared/lib/format'
import { Panel, Skeleton, StateBlock } from '@/shared/ui'

import s from './ColumnsPanel.module.css'

interface ColumnsPanelProps {
  /** Топливные колонки — реальный API (ТЗ п. 4.2). */
  columns: Columns | undefined
  isLoading: boolean
  error: unknown
  onRetry: () => void
  className?: string
}

const SKELETON_COUNT = 6

/** Автомобилей у колонки сейчас — только при status = ok, иначе показаний нет (ТЗ п. 8). */
const vehicleCount = (column: Column) => apiValueNumber(column.vehicles)

/**
 * Карточка колонки. Нет автомобилей — «Очереди нет», есть — «N авто»; данных по колонке
 * нет — «Нет актуальных данных» и время последнего успешного обновления (ТЗ п. 4.2).
 */
function Pump({ column }: { column: Column }) {
  const count = vehicleCount(column)
  const tone = count === undefined ? s.off : count > 0 ? s.busy : s.free
  const lastAvailableAt = column.last_available?.updated_at
  const content = (
    <>
      <span className={s.pumpId}>
        {/* На телефоне слово «Колонка» скрыто: остаётся «№N», иначе не хватает места индикатору. */}
        <span className={s.pumpWord}>Колонка </span>№{column.column_number}
      </span>
      <span className={s.led} />
      {count !== undefined ? (
        <>
          <span className={s.queue}>
            {count}
            <small>авто</small>
          </span>
          <span className={s.status}>{count > 0 ? 'очередь' : 'Очереди нет'}</span>
        </>
      ) : (
        <>
          <span className={s.placeholder}>Нет актуальных данных</span>
          {lastAvailableAt && <span className={s.stale}>{formatUpdatedAt(lastAvailableAt)}</span>}
        </>
      )}
    </>
  )
  // Карточка колонки — не ссылка: с «Главной» в «Поток автомобилей» ведёт только KPI «Автомобили» (ТЗ п. 9).
  return <div className={clsx(s.pump, tone)}>{content}</div>
}

export function ColumnsPanel({ columns, isLoading, error, onRetry, className }: ColumnsPanelProps) {
  if (error) {
    return (
      <Panel title="Колонки" className={className}>
        <StateBlock kind="error" message={getErrorMessage(error)} onRetry={onRetry} />
      </Panel>
    )
  }

  const pumps = columns?.columns ?? []
  // В сводку входят только колонки с показаниями; без данных — не ноль, а пропуск (ТЗ п. 8).
  const counts = pumps.map(vehicleCount).filter((n): n is number => n !== undefined)
  const busy = counts.filter((n) => n > 0).length
  const carsNow = counts.reduce((sum, n) => sum + n, 0)

  return (
    <Panel
      title="Колонки"
      subtitle={columns ? `${busy} с очередью · ${carsNow} авто` : undefined}
      className={clsx(s.panel, className)}
      bodyClassName={s.body}
    >
      <div className={s.canopy}>
        <div className={s.row}>
          {isLoading
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <Skeleton key={i} height={68} style={{ borderRadius: 8 }} />
              ))
            : pumps.map((column) => <Pump key={column.column_number} column={column} />)}
        </div>
        {!isLoading && pumps.length === 0 && <div className={s.noData}>Нет актуальных данных</div>}
      </div>

      {/* Время последнего снимка состояния — внизу панели (просьба заказчика). */}
      <span className={s.updated}>
        {columns?.updated_at ? formatUpdatedAt(columns.updated_at) : 'Обновлено: —'}
      </span>
    </Panel>
  )
}
